// Cartons — every organisation's cartons, and the changes that cross
// organisations (V2 #100, on the backend's #94 and #99).
//
// The table is the screen: read-only rows, a gear on each opening one sheet
// (CartonSheet) that closes on save. Whose cartons are shown is a VIEW the
// backend remembers for this admin and records whenever it changes, so it is
// sent only when chosen here; filters, search, sort and pages are not a view.

import React from 'react';
import { api, errorText } from '../api';
import { useAsync, useDebounced } from '../useAsync';
import { ago, num } from '../format';
import CartonSheet from '../ui/CartonSheet';
import {
  Chips, Empty, ErrorState, Icon, PageHead, Pill, RefreshButton, Search, Skeleton, Button,
} from '../ui/primitives';
import {
  KINDS, NO_FILTERS, PAGE, SCOPES, SORTS, STATUSES, STATUS_WORDS, duplicatesLabel, kindLabel,
  normaliseView, pagerText, sameView, sizeLabel, statusOf, viewLabel,
} from '../cartons';

export default function Cartons({ query }) {
  const [filters, setFilters] = React.useState({ ...NO_FILTERS, q: query.q || '' });
  const debouncedQ = useDebounced(filters.q, 280);
  const [page, setPage] = React.useState(0);
  const [picking, setPicking] = React.useState(false);    // "One organisation", none picked yet
  const [open, setOpen] = React.useState(null);
  // A view just chosen, sent with the next load only (then the backend
  // remembers it); a nonce makes the choice itself trigger that load.
  const pendingView = React.useRef(null);
  const [nonce, setNonce] = React.useState(0);
  const orgs = useAsync(() => api.orgs({ status: 'all' }), []);

  // A changed filter must reset paging, or page 3 of the old query silently
  // becomes an empty page 3 of the new one.
  React.useEffect(() => { setPage(0); },
    [debouncedQ, filters.owner, filters.status, filters.kind, filters.duplicatesOnly, filters.sort]);

  const { data, error, loading, at, reload } = useAsync(() => {
    const view = pendingView.current;
    pendingView.current = null;
    return api.cartons({ view, filters: { ...filters, q: debouncedQ }, page, pageSize: PAGE });
  }, [nonce, debouncedQ, filters.owner, filters.status, filters.kind, filters.duplicatesOnly, filters.sort, page]);

  const view = normaliseView(data?.view);
  const orgList = Array.isArray(orgs.data) ? orgs.data : [];
  const orgsById = Object.fromEntries(orgList.map((o) => [o.id, o]));
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  function choose(next) {
    if (!data || sameView(data.view, next)) return;
    pendingView.current = normaliseView(next);
    setPage(0);
    setNonce((n) => n + 1);
  }
  function chooseScope(scope) {
    if (scope === 'org') { setPicking(true); return; }
    setPicking(false);
    choose({ scope, include_drafts: view.include_drafts });
  }

  const items = data?.items || [];
  const shown = items.length;

  return (
    <>
      <PageHead title="Cartons"
                sub={data ? `${num(data.total)} carton${data.total === 1 ? '' : 's'} · ${viewLabel(data.view, orgsById)}`
                  : 'Every organisation’s cartons'}
                action={<RefreshButton onClick={reload} loading={loading} at={at} />} />

      <div className="stack" style={{ marginBottom: 14 }}>
        <Chips options={SCOPES} value={picking ? 'org' : view.scope} onChange={chooseScope} />
        {(picking || view.scope === 'org') && (
          <select className="select" aria-label="Organisation"
                  value={picking && view.scope !== 'org' ? '' : (view.org_id || '')}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setPicking(false);
                    choose({ scope: 'org', org_id: e.target.value, include_drafts: view.include_drafts });
                  }}>
            <option value="">Choose an organisation…</option>
            {orgList.map((o) => (
              <option key={o.id} value={o.id}>{o.name}{o.is_active === false ? ' (deactivated)' : ''}</option>
            ))}
          </select>
        )}
        <label className="cartons-check" style={{ opacity: view.scope === 'mine' ? 0.5 : 1 }}>
          <input type="checkbox" checked={view.include_drafts} disabled={view.scope === 'mine'}
                 onChange={(e) => choose({ ...view, include_drafts: e.target.checked })} />
          Include other people’s drafts
        </label>
        <div className="dim" style={{ fontSize: 12 }}>
          Your choice of whose cartons to show is remembered, and each change is recorded.
          Click an owner’s name to show only their cartons.
        </div>

        <Search value={filters.q} onChange={(q) => set({ q })} placeholder="Search name, owner or organisation" />
        <Chips options={STATUSES} value={filters.status} onChange={(status) => set({ status })} />
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {filters.owner && (
            <button type="button" className="chip" aria-pressed="true" title="Show every owner’s cartons"
                    onClick={() => set({ owner: null, ownerName: '' })}>
              Owner: {filters.ownerName || 'one owner'} ×
            </button>
          )}
          <select className="select cartons-inline" aria-label="Kind" value={filters.kind}
                  onChange={(e) => set({ kind: e.target.value })}>
            {KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="select cartons-inline" aria-label="Sort" value={filters.sort}
                  onChange={(e) => set({ sort: e.target.value })}>
            {SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label className="cartons-check">
            <input type="checkbox" checked={filters.duplicatesOnly}
                   onChange={(e) => set({ duplicatesOnly: e.target.checked })} />
            Duplicates only
          </label>
        </div>
      </div>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={5} />}

      {data && shown === 0 && (
        <Empty title="No cartons">Nothing in this view matches. Try another search, filter or view.</Empty>
      )}

      {data && shown > 0 && (
        <div className="card">
          <div className="cartons-wrap">
            <table className="cartons-table">
              <thead>
                <tr>
                  <th>Organisation</th><th>Owner</th><th>Name</th><th>Status</th><th>Kind</th>
                  <th>Size</th><th>Added</th><th>Duplicates</th>
                  <th className="cartons-gear-cell" aria-label="Change" />
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const st = STATUS_WORDS[statusOf(c)];
                  const dups = duplicatesLabel(c);
                  return (
                    <tr key={c.id}>
                      <td>{c.org_name || '—'}</td>
                      <td>
                        {c.owner_name
                          ? (
                            <button type="button" className="cartons-owner" title="Show only this owner’s cartons"
                                    onClick={() => set({ owner: c.owner_id, ownerName: c.owner_name })}>
                              {c.owner_name}
                            </button>
                          )
                          : '—'}
                      </td>
                      <td className="cartons-name" title={c.name}>{c.name}</td>
                      <td><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td>{kindLabel(c)}</td>
                      <td>{sizeLabel(c)}</td>
                      <td>{ago(c.created_at)}</td>
                      <td>{dups ? <Pill tone="warn">{dups}</Pill> : <span className="dim">—</span>}</td>
                      <td className="cartons-gear-cell">
                        <button type="button" className="btn ghost icon cartons-gear"
                                title="Open, unshare, delete or restore"
                                aria-label={`Change ${c.name}`} onClick={() => setOpen(c)}>
                          <Icon name="gear" size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.total > PAGE && (
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <Button small disabled={page === 0 || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
          <span className="mono dim" style={{ fontSize: 11 }}>
            {pagerText({ page, pageSize: PAGE, total: data.total, shown })}
          </span>
          <Button small disabled={page * PAGE + shown >= data.total || loading}
                  onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {open && (
        <CartonSheet carton={open} onClose={() => setOpen(null)}
                     onDone={() => { setOpen(null); reload(); }} />
      )}
    </>
  );
}
